import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ViewIcon,
  ArrowRightIcon,
  SearchIcon,
  CloseIcon,
  ApplicationsIcon,
} from "../../../Icons";
import useCurrentUser from "../../../hooks/useCurrentUser";
import {
  PAGE_SHELL,
  PAGE_HEADER_ROW,
  PAGE_SUBTITLE,
  PAGE_ACTIONS,
} from "../../../ui/pageChrome";
import {
  APPLICATION_THEME,
  APPLICATION_FORM_ICON,
} from "../applicationTheme";
import { getVisibleGeneralForms } from "./generalFormsCatalog";

const GeneralForms = () => {
  const navigate = useNavigate();
  const { isAdmin } = useCurrentUser();
  const [search, setSearch] = useState("");

  const libraryForms = useMemo(() => getVisibleGeneralForms(), []);

  const filteredForms = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return libraryForms;
    return libraryForms.filter(
      (f) =>
        f.title.toLowerCase().includes(q) ||
        f.description.toLowerCase().includes(q),
    );
  }, [search, libraryForms]);

  return (
    <div className={PAGE_SHELL}>
      <div className={PAGE_HEADER_ROW}>
        <div>
          <h1 className={`text-2xl font-bold ${APPLICATION_THEME.section.title}`}>
            General Forms
          </h1>
          <p className={PAGE_SUBTITLE}>
            Select a form to proceed with the application process
          </p>
        </div>
        <div className={PAGE_ACTIONS}>
          <button
            type="button"
            onClick={() => navigate("/general-forms/list")}
            className={`inline-flex items-center justify-center gap-1.5 h-9 px-3 text-sm font-semibold rounded-md transition-colors ${APPLICATION_THEME.button.primary}`}
            title={
              isAdmin
                ? "View all submitted forms"
                : "View your submitted forms"
            }
          >
            <ViewIcon size={15} />
            View Forms
          </button>
        </div>
      </div>

      <div
        className={`bg-white rounded-xl border shadow-sm overflow-hidden ${APPLICATION_THEME.section.wrap}`}
      >
        <div
          className={`flex items-center gap-2 h-11 px-4 border-b box-border ${APPLICATION_THEME.section.head}`}
        >
          <span
            className={`w-0.5 h-4 rounded-full shrink-0 ${APPLICATION_THEME.section.bar}`}
            aria-hidden
          />
          <span className={`shrink-0 ${APPLICATION_THEME.section.icon}`}>
            <ApplicationsIcon size={14} />
          </span>
          <h2
            className={`min-w-0 flex-1 text-xs font-semibold uppercase tracking-wide truncate ${APPLICATION_THEME.section.title}`}
          >
            Form Library
          </h2>
          <span
            className={`inline-flex items-center justify-center h-5 min-w-[1.25rem] px-1.5 rounded text-[10px] font-semibold tabular-nums shrink-0 ${APPLICATION_THEME.section.badge}`}
          >
            {filteredForms.length}
          </span>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-2.5 px-3 py-2.5 border-b border-indigo-100">
          <div className="relative flex-1 min-w-0">
            <SearchIcon
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
            />
            <input
              type="text"
              placeholder="Search forms..."
              className={`w-full h-8 pl-9 pr-8 text-sm bg-white border border-indigo-200 rounded-md text-slate-800 placeholder:text-slate-400 outline-none transition-colors ${APPLICATION_THEME.fieldFocus}`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search ? (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded text-slate-400 hover:text-slate-600"
                aria-label="Clear search"
              >
                <CloseIcon size={14} />
              </button>
            ) : null}
          </div>
        </div>

        {filteredForms.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-slate-500">
            No forms match your search
          </div>
        ) : (
          <div className="p-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
            {filteredForms.map((form) => {
              const Icon = form.icon;
              return (
                <button
                  key={form.id}
                  type="button"
                  onClick={() => navigate(form.route)}
                  className={`flex items-center gap-2.5 rounded-xl border bg-white px-3 py-2.5 text-left shadow-sm transition-colors group min-h-[72px] ${APPLICATION_THEME.section.wrap} hover:border-indigo-300 hover:bg-indigo-50/40`}
                >
                  <span
                    className={`inline-flex h-9 w-9 items-center justify-center rounded-md shrink-0 ${APPLICATION_FORM_ICON.color} ${APPLICATION_FORM_ICON.textColor}`}
                  >
                    <Icon size={18} strokeWidth={2} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-900 group-hover:text-indigo-700 leading-snug line-clamp-2">
                      {form.title}
                    </p>
                    <p className="text-[11px] text-slate-500 mt-0.5 truncate">
                      {form.description}
                    </p>
                  </div>
                  <ArrowRightIcon
                    size={14}
                    className="text-slate-300 group-hover:text-indigo-500 shrink-0"
                  />
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default GeneralForms;
